import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
    Typography,
    Button,
    FormControlLabel,
    RadioGroup,
    Radio,
    FormControl,
    Chip,
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from "@material-ui/core";
import {
    CheckCircle,
    Warning,
    InfoOutlined,
    Description,
    ArrowBack,
    Check,
    Close,
    AccessTime,
    ExpandMore,
    Person,
    LinkOff
} from "@material-ui/icons";
import { trackPromise } from "react-promise-tracker";

import LoadingProgress from "../../LoadingProgress";
import useSnackbar from "../../../hooks/useSnackbar";

import reviewService from "../../../domain/services/request/request.review.service";
import loginService from "../../../domain/services/login.service";
import { stepsSolicitation } from "../../../domain/constant/steps.solicitation.constant";

// --- Styles ---

const fadeInStyle = `
    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-8px); }
        to { opacity: 1; transform: translateY(0); }
    }
`;

// --- Utility functions ---

const extractPageInfo = (text) => {
    const pages = [];
    let match;

    // Novo formato: [páginas 1-10]
    const bracketRegex = /\[páginas ([^\]]+)\]/g;
    while ((match = bracketRegex.exec(text)) !== null) {
        pages.push(match[1].trim());
    }

    // Formato antigo: páginas 1-8:  (sem colchetes, com dois-pontos)
    if (pages.length === 0) {
        const colonRegex = /páginas ([^:]+):/g;
        while ((match = colonRegex.exec(text)) !== null) {
            pages.push(match[1].trim());
        }
    }

    return pages;
};

const removePageMarkers = (text) => {
    return text
        .replace(/\[páginas [^\]]+\]\s*/g, '')   // novo formato: [páginas X-Y]
        .replace(/páginas [^:]+:/g, '')            // formato antigo: páginas X:
        .replace(/- - - /g, '')                    // separador antigo
        .replace(/^\d+\.\s*/, '')                  // numeração: "1. ", "2. "
        .trim();
};

const getReviewLabel = (num) => `${num}ª Revisão`;

const getCorrectionVersionLabel = (version) => {
    if (!version || version === 0) return null;
    return `Cor ${version}`;
};

const groupReviewsByFaciliter = (reviews, requestMembers) => {
    if (!reviews || !reviews.length) return [];

    const groupsMap = {};
    const groupOrder = [];

    // Criar set de userIds dos members atualmente vinculados
    const linkedMemberIds = new Set((requestMembers || []).map(m => m.userId));

    reviews.forEach((review) => {
        const key = review.senderUserId;
        if (!groupsMap[key]) {
            groupsMap[key] = {
                faciliterId: review.senderUserId,
                faciliterName: review.senderName || 'Faciliter',
                isUnlinked: false,
                unlinkInfo: null,
                reviews: []
            };
            groupOrder.push(key);
        }

        const group = groupsMap[key];
        group.reviews.push({
            ...review,
            reviewNumber: group.reviews.length + 1
        });
    });

    // Determinar isUnlinked baseado nos members atualmente vinculados
    // Se o faciliter está em request.members, ele está vinculado (mesmo que tenha sido desvinculado antes)
    // Caso contrário, verificamos a última review
    groupOrder.forEach(key => {
        const group = groupsMap[key];
        const isCurrentlyLinked = linkedMemberIds.has(group.faciliterId);
        const lastReview = group.reviews[group.reviews.length - 1];

        if (isCurrentlyLinked) {
            group.isUnlinked = false;
        } else if (lastReview?.unlinkedAt) {
            group.isUnlinked = true;
            group.unlinkInfo = lastReview.unlinkInfo;
        }
    });

    return groupOrder
        .map(key => groupsMap[key])
        .sort((a, b) => {
            // Faciliters vinculados (isUnlinked=false) vêm primeiro
            if (a.isUnlinked === b.isUnlinked) return 0;
            return a.isUnlinked ? 1 : -1;
        });
};

const reviewNeedsResponse = (review) => {
    return review?.checklist &&
        review.checklist.length > 0 &&
        review.checklist.some(item => !item.answer);
};

// --- Sub-components ---

const SOURCE_LABEL_STYLES = {
    'Documento enviado': { background: '#dbeafe', color: '#1e40af' },
    'Formulário do cliente': { background: '#ede9fe', color: '#5b21b6' },
    'Contexto do caso': { background: '#ccfbf1', color: '#0f766e' },
    'Resposta do faciliter': { background: '#fef3c7', color: '#92400e' },
    'Validação automática': { background: '#e0e7ff', color: '#3730a3' },
    'Sem rastro disponível (revisão anterior à v2)': { background: '#f1f5f9', color: '#64748b' }
};

const getSourceChipStyle = (label) => {
    if (!label) return SOURCE_LABEL_STYLES['Documento enviado'];
    return SOURCE_LABEL_STYLES[label] || { background: '#f1f5f9', color: '#475569' };
};

const LEGACY_SOURCE_LABEL_TEXT = 'Sem rastro disponível (revisão anterior à v2)';

const ChecklistItemAuditBlock = ({ item, isFirstReviewInRequest }) => {
    if (!item) return null;

    const sourceLabel = item.sourceLabel;
    const evidenceSnippet = item.evidenceSnippet;
    const evidencePages = item.evidencePages;
    const isLegacy = item.source === 'legacy' || sourceLabel === LEGACY_SOURCE_LABEL_TEXT;
    const isRepeat = Boolean(item.isRepeatOf);
    const firstSeenVersion = item.firstSeenVersion;
    const hasValidFirstSeenVersion = typeof firstSeenVersion === 'number' && firstSeenVersion >= 1;
    const isNewInSecondReview = !isLegacy && !isRepeat && isFirstReviewInRequest === false;

    const hasAnyAuditInfo = sourceLabel || evidenceSnippet || evidencePages || isRepeat || isNewInSecondReview;
    if (!hasAnyAuditInfo) return null;

    const evidencePagesChip = evidencePages && (
        <Chip
            label={evidencePages.replace(/^páginas\s*/i, 'Pág. ')}
            size="small"
            style={{
                backgroundColor: '#e0f2fe',
                color: '#0369a1',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: '20px'
            }}
        />
    );

    const chipStyle = getSourceChipStyle(sourceLabel);

    return (
        <div
            style={{
                marginTop: '10px',
                marginLeft: '42px',
                padding: '12px 14px',
                background: '#f8fafc',
                borderRadius: '6px',
                borderLeft: '3px solid #cbd5e1'
            }}
        >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: evidenceSnippet ? '10px' : 0 }}>
                {sourceLabel && (
                    <Chip
                        label={sourceLabel}
                        size="small"
                        style={{
                            backgroundColor: chipStyle.background,
                            color: chipStyle.color,
                            fontWeight: 600,
                            fontSize: '0.72rem',
                            height: '22px'
                        }}
                    />
                )}
                {isRepeat && (
                    <Chip
                        label={hasValidFirstSeenVersion
                            ? `Repetição da ${firstSeenVersion}ª revisão`
                            : 'Repetição de revisão anterior'}
                        size="small"
                        style={{
                            backgroundColor: '#fef3c7',
                            color: '#92400e',
                            fontWeight: 600,
                            fontSize: '0.72rem',
                            height: '22px'
                        }}
                    />
                )}
                {isNewInSecondReview && (
                    <Chip
                        label="Ponto novo nesta revisão"
                        size="small"
                        style={{
                            backgroundColor: '#dcfce7',
                            color: '#166534',
                            fontWeight: 600,
                            fontSize: '0.72rem',
                            height: '22px'
                        }}
                    />
                )}
            </div>
            {evidenceSnippet && (
                <div
                    style={{
                        padding: '8px 12px',
                        background: '#ffffff',
                        borderLeft: '2px solid #94a3b8',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '10px'
                    }}
                >
                    <Typography
                        variant="body2"
                        style={{
                            flex: 1,
                            fontStyle: 'italic',
                            color: '#475569',
                            fontSize: '0.82rem',
                            lineHeight: 1.6
                        }}
                    >
                        "{evidenceSnippet}"
                    </Typography>
                    {evidencePagesChip}
                </div>
            )}
            {!evidenceSnippet && evidencePagesChip && (
                <div style={{ display: 'flex', marginTop: '6px' }}>
                    {evidencePagesChip}
                </div>
            )}
        </div>
    );
};

const ReviewAuditHeader = ({ review }) => {
    if (!review) return null;

    const createdAt = review.createdAt;
    const unlinkedAt = review.unlinkedAt;
    const faciliterAnswered = review.faciliterAnsweredPrevious;

    const formatTimestamp = (value) => {
        if (!value) return null;
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return null;
        return date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
    };

    const createdLabel = formatTimestamp(createdAt);
    const unlinkedLabel = formatTimestamp(unlinkedAt);

    const showFaciliterChip = faciliterAnswered !== null && faciliterAnswered !== undefined;

    if (!createdLabel && !unlinkedLabel && !showFaciliterChip) return null;

    return (
        <div
            style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginBottom: '14px',
                padding: '10px 14px',
                background: '#f1f5f9',
                borderRadius: '6px'
            }}
        >
            {showFaciliterChip && (
                <Chip
                    label={faciliterAnswered
                        ? 'Faciliter respondeu checklist anterior: Sim'
                        : 'Faciliter respondeu checklist anterior: Não'}
                    size="small"
                    style={{
                        backgroundColor: faciliterAnswered ? '#dcfce7' : '#fee2e2',
                        color: faciliterAnswered ? '#166534' : '#991b1b',
                        fontWeight: 600,
                        fontSize: '0.72rem',
                        height: '22px'
                    }}
                />
            )}
            {createdLabel && (
                <Typography variant="caption" style={{ color: '#64748b', fontSize: '0.75rem', alignSelf: 'center' }}>
                    Gerada em {createdLabel}
                </Typography>
            )}
            {unlinkedLabel && (
                <Typography variant="caption" style={{ color: '#dc2626', fontSize: '0.75rem', alignSelf: 'center' }}>
                    Desvinculado em {unlinkedLabel}
                </Typography>
            )}
        </div>
    );
};

const ChecklistItem = ({ item, index, readOnly, responseValue, onResponseChange }) => {
    const showRadios = !readOnly && loginService.isFaciliter();

    return (
        <div style={{ padding: '18px 0', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                <Typography
                    variant="body2"
                    style={{
                        color: '#94a3b8', fontWeight: 600, fontSize: '0.9rem',
                        minWidth: '28px', marginTop: '2px'
                    }}
                >
                    {index + 1}.
                </Typography>
                <div style={{ flex: 1 }}>
                    <Typography
                        variant="body1"
                        style={{ color: '#1e293b', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '4px' }}
                    >
                        {item.description}
                    </Typography>

                    {showRadios && (
                        <div style={{ marginTop: '12px' }}>
                            <FormControl component="fieldset">
                                <RadioGroup
                                    value={responseValue || ''}
                                    onChange={(e) => onResponseChange(index, e.target.value)}
                                    style={{ gap: '4px' }}
                                >
                                    <FormControlLabel
                                        value="adjusted"
                                        control={<Radio color="primary" size="small" />}
                                        label={
                                            <Typography style={{ fontSize: '0.875rem', color: '#475569' }}>
                                                Ajustei esse ponto no documento
                                            </Typography>
                                        }
                                    />
                                    <FormControlLabel
                                        value="incoherent"
                                        control={<Radio color="primary" size="small" />}
                                        label={
                                            <Typography style={{ fontSize: '0.875rem', color: '#475569' }}>
                                                Essa sugestão não condiz com o que foi pedido pelo cliente
                                            </Typography>
                                        }
                                    />
                                </RadioGroup>
                            </FormControl>
                        </div>
                    )}

                    {!showRadios && item.answer && (
                        <Typography
                            variant="body2"
                            style={{
                                marginTop: '8px', fontSize: '0.875rem',
                                color: item.answer === 'adjusted' ? '#16a34a' : '#dc2626',
                                fontWeight: 500
                            }}
                        >
                            {item.answer === 'adjusted'
                                ? 'Ajustou esse ponto no documento'
                                : 'Considerou incoerente com o pedido do cliente'
                            }
                        </Typography>
                    )}
                </div>
            </div>
        </div>
    );
};

const JustificationSection = ({ justification }) => {
    if (!justification) return null;

    return (
        <div style={{ marginTop: '10px', paddingTop: '10px' }}>
            <Typography
                variant="body2"
                style={{ fontWeight: 600, color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '12px' }}
            >
                Justificativa
            </Typography>

            <div style={{ paddingLeft: '12px', borderLeft: '3px solid #e2e8f0' }}>
                {(justification || 'Nenhuma justificativa fornecida.')
                    .split(/\n|\|/)
                    .map((line, index) => {
                        const trimmedLine = line.trim();
                        if (!trimmedLine) return null;

                        const linePages = extractPageInfo(line);
                        const cleanText = removePageMarkers(line);

                        return (
                            <div key={index} style={{ marginBottom: '10px' }}>
                                {linePages.length > 0 && (
                                    <div style={{ marginBottom: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                        {linePages.map((page, i) => (
                                            <Chip
                                                key={i}
                                                label={`Pág. ${page}`}
                                                size="small"
                                                style={{ backgroundColor: '#dbeafe', color: '#1e40af', fontSize: '0.7rem', height: '20px', fontWeight: 600 }}
                                            />
                                        ))}
                                    </div>
                                )}
                                <Typography
                                    variant="body2"
                                    style={{ color: '#475569', fontSize: '0.9rem', lineHeight: 1.7 }}
                                >
                                    {cleanText}
                                </Typography>
                            </div>
                        );
                    })}
            </div>
        </div>
    );
};

const UnlinkSection = ({ unlinkReasonItems, unlinkInfo, isFirst }) => {
    const hasReasons = unlinkReasonItems && unlinkReasonItems.length > 0;
    const hasInfo = unlinkInfo?.reason;

    if (!hasReasons && !hasInfo) return null;

    return (
        <div style={{
            marginTop: isFirst ? '0' : '20px',
            paddingTop: isFirst ? '0' : '20px',
            marginBottom: isFirst ? '24px' : '0',
            borderTop: isFirst ? 'none' : '1px solid #fee2e2',
            paddingBottom: isFirst ? '20px' : '0',
            borderBottom: isFirst ? '1px solid #fee2e2' : 'none'
        }}>
            <Typography
                variant="body2"
                style={{ fontWeight: 600, color: '#dc2626', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '12px' }}
            >
                Motivo da Desvinculação
            </Typography>

            {hasInfo && (
                <Typography
                    variant="body2"
                    style={{ color: '#991b1b', fontSize: '0.875rem', marginBottom: hasReasons ? '12px' : '0', lineHeight: 1.6 }}
                >
                    {unlinkInfo.reason}
                </Typography>
            )}

            {hasReasons && (
                <div style={{ paddingLeft: '12px', borderLeft: '3px solid #fca5a5' }}>
                    {unlinkReasonItems.map((reason, i) => (
                        <Typography
                            key={i}
                            variant="body2"
                            style={{ color: '#991b1b', fontSize: '0.875rem', lineHeight: 1.7, marginBottom: '4px' }}
                        >
                            {i + 1}. {reason}
                        </Typography>
                    ))}
                </div>
            )}
        </div>
    );
};

const ReviewCard = ({ review, readOnly, responses, onResponseChange, isLast, isInternal }) => {
    const correctionLabel = getCorrectionVersionLabel(review.correctionVersion);

    return (
    <div style={{ marginBottom: isLast ? 0 : '58px' }}>
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px',
            paddingBottom: '12px',
            borderBottom: '2px solid #e2e8f0'
        }}>
            <Typography
                variant="h6"
                style={{
                    fontWeight: 700,
                    color: '#0f172a',
                    fontSize: '1rem',
                    letterSpacing: '-0.02em'
                }}
            >
                {getReviewLabel(review.reviewNumber)}
            </Typography>
            {correctionLabel && (
                <Chip
                    label={correctionLabel}
                    size="small"
                    style={{ backgroundColor: '#e0f2fe', color: '#0369a1', fontWeight: 600, fontSize: '0.75rem', height: '24px' }}
                />
            )}
            {review.hasMajorErrors && (
                <Chip
                    label="Documento inválido"
                    size="small"
                    style={{ backgroundColor: '#fef2f2', color: '#dc2626', fontWeight: 600, fontSize: '0.75rem', height: '24px' }}
                />
            )}
        </div>

        {isInternal && <ReviewAuditHeader review={review} />}

        {(review.checklist || []).map((item, index) => (
            <React.Fragment key={index}>
                <ChecklistItem
                    item={item}
                    index={index}
                    readOnly={readOnly}
                    responseValue={responses ? responses[index] : undefined}
                    onResponseChange={onResponseChange}
                />
                {isInternal && (
                    <ChecklistItemAuditBlock
                        item={item}
                        isFirstReviewInRequest={review.faciliterAnsweredPrevious === null}
                    />
                )}
            </React.Fragment>
        ))}

        <JustificationSection justification={review.justification} />
    </div>
    );
};

const FaciliterGroup = ({ group, defaultExpanded, currentReview, responses, onResponseChange, isInternal }) => {
    const reviewCount = group.reviews?.length || 0;

    // Coletar todos os unlinkReasonItems de todas as reviews
    const allUnlinkReasons = (group.reviews || [])
        .filter(r => r.unlinkReasonItems && r.unlinkReasonItems.length > 0)
        .flatMap(r => r.unlinkReasonItems);

    const hasUnlinkReasons = allUnlinkReasons.length > 0;

    return (
        <Accordion
            defaultExpanded={defaultExpanded}
            style={{
                marginBottom: '16px',
                boxShadow: 'none',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                overflow: 'hidden',
                '&:before': { display: 'none' }
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMore style={{ color: '#64748b' }} />}
                style={{
                    padding: '12px 20px',
                    minHeight: '64px',
                    backgroundColor: group.isUnlinked ? '#fef2f2' : '#f8fafc'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                    <Person style={{ fontSize: '1.25rem', color: group.isUnlinked ? '#dc2626' : '#64748b' }} />
                    <div style={{ flex: 1 }}>
                        <Typography style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.95rem', marginBottom: '2px' }}>
                            {group.faciliterName}
                        </Typography>
                        <Typography variant="body2" style={{ color: '#64748b', fontSize: '0.8rem' }}>
                            {reviewCount === 1 ? '1 revisão' : `${reviewCount} revisões`}
                        </Typography>
                    </div>
                    {group.isUnlinked && (
                        <Chip
                            label="Desvinculado"
                            size="small"
                            style={{ backgroundColor: '#fee2e2', color: '#dc2626', fontWeight: 600, fontSize: '0.75rem', height: '24px' }}
                        />
                    )}
                </div>
            </AccordionSummary>
            <AccordionDetails style={{ display: 'block', padding: '24px' }}>
                {group.isUnlinked && (
                    <UnlinkSection
                        unlinkReasonItems={hasUnlinkReasons ? allUnlinkReasons : undefined}
                        unlinkInfo={group.unlinkInfo || (group.isUnlinked ? { reason: 'Faciliter desvinculado do processo.' } : null)}
                        isFirst={true}
                    />
                )}

                {(group.reviews || []).map((review, index) => {
                    const isCurrentReview = currentReview && currentReview.documentId === review.documentId;
                    return (
                        <ReviewCard
                            key={review.documentId || index}
                            review={review}
                            readOnly={!isCurrentReview}
                            responses={isCurrentReview ? responses : undefined}
                            onResponseChange={isCurrentReview ? onResponseChange : undefined}
                            isLast={index === group.reviews.length - 1}
                            isInternal={isInternal}
                        />
                    );
                })}
            </AccordionDetails>
        </Accordion>
    );
};

// --- Main component ---

const RequestServiceReviewView = ({ requestId, mainRequestId, onNavigate, request }) => {
    const { alert, setAlert } = useSnackbar();
    const history = useHistory();

    const [allReviews, setAllReviews] = useState([]);
    const [responses, setResponses] = useState({});
    const [loading, setLoading] = useState(true);
    const [isButtonLoading, setIsButtonLoading] = useState(false);

    useEffect(() => {
        if (requestId) fetchData();
    }, [requestId]);

    const getQueryParams = () => {
        const urlParams = new URLSearchParams(window.location.search);
        return {
            returnTo: urlParams.get('returnTo'),
            actionType: urlParams.get('actionType')
        };
    };

    const getButtonText = () => {
        const { returnTo, actionType } = getQueryParams();
        if (returnTo === 'conclude' && actionType) return 'Confirmar e Finalizar';
        return 'Salvar Respostas';
    };

    const fetchData = async () => {
        if (!requestId) return;
        setLoading(true);

        try {
            const reviews = await trackPromise(
                reviewService.findAllReviewsByRequestId(mainRequestId || requestId)
            );

            setAllReviews(reviews || []);

            if (loginService.isFaciliter()) {
                const currentUser = loginService.getUserAuthentication();
                const userReviews = (reviews || []).filter(r => r.senderUserId === currentUser?.id);
                const lastReview = userReviews[userReviews.length - 1];

                if (lastReview && reviewNeedsResponse(lastReview)) {
                    const initialResponses = {};
                    lastReview.checklist.forEach((item, index) => {
                        initialResponses[index] = item?.answer || '';
                    });
                    setResponses(initialResponses);
                }
            }
        } catch (error) {
            setAllReviews([]);
            if (setAlert) {
                setAlert('Erro ao carregar dados da revisão. Tente novamente.', 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResponseChange = (itemIndex, value) => {
        setResponses(prev => ({ ...prev, [itemIndex]: value }));
    };

    const getCurrentReview = () => {
        if (!loginService.isFaciliter())
            return null;
        const currentUser = loginService.getUserAuthentication();
        const userReviews = allReviews.filter(r => r.senderUserId === currentUser?.id);
        const lastReview = userReviews[userReviews.length - 1];
        return lastReview && reviewNeedsResponse(lastReview) ? lastReview : null;
    };

    const hasValidResponses = () => {
        const current = getCurrentReview();
        if (!current) return false;
        return current.checklist.every((_, index) =>
            responses[index] && responses[index] !== ''
        );
    };

    const handleConfirm = async () => {
        setIsButtonLoading(true);

        try {
            const current = getCurrentReview();
            if (current) {
                await reviewService.saveReview(requestId, responses);
            }
            setAlert("Serviço concluído com sucesso!", "success");

            const { returnTo, actionType } = getQueryParams();
            if (returnTo === 'conclude' && actionType) {
                const currentPath = window.location.pathname;
                history.push(`${currentPath}?conclude=true&actionType=${actionType}#details`);
            } else if (onNavigate) {
                onNavigate('details');
            }
        } catch (error) {
            setAlert("Erro ao processar. Tente novamente.", "error");
        } finally {
            setIsButtonLoading(false);
        }
    };

    // --- Derived state ---

    const faciliterGroups = groupReviewsByFaciliter(allReviews, request?.members);
    const hasReviews = allReviews.length > 0;
    const lastReview = hasReviews ? allReviews[allReviews.length - 1] : null;
    const isApproved = lastReview && (!lastReview.checklist || lastReview.checklist.length === 0);
    const currentReview = getCurrentReview();
    const isInternal = loginService.isInternal();
    const isFaciliter = loginService.isFaciliter();
    const { returnTo: currentReturnTo, actionType: currentActionType } = getQueryParams();
    const isFromConcludeFlow = currentReturnTo === 'conclude' && !!currentActionType;

    // --- Inner display components ---

    const ViewHeader = () => {
        const handleBackNavigation = () => {
            const { returnTo } = getQueryParams();
            if (returnTo === 'conclude') {
                history.push(`${window.location.pathname}#details`);
            } else if (onNavigate) {
                onNavigate('details');
            }
        };

        return (
            <Box mb={4}>
                {onNavigate && (
                    <Button
                        startIcon={<ArrowBack style={{ fontSize: '1.1rem' }} />}
                        onClick={handleBackNavigation}
                        style={{ textTransform: 'none', color: '#64748b', fontSize: '0.875rem', padding: '6px 12px' }}
                    >
                        Voltar aos Detalhes
                    </Button>
                )}
            </Box>
        );
    };

    // --- Render ---

    if (loading) {
        return (
            <div>
                <style>{fadeInStyle}</style>
                <ViewHeader />
                <LoadingProgress />
            </div>
        );
    }

    // No reviews at all
    if (!hasReviews) {
        const isInReview = request?.status === stepsSolicitation.REVIEW && !!request.enterAutomaticReviewFlowAt;

        return (
            <div style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
                <style>{fadeInStyle}</style>
                <ViewHeader />
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b' }}>
                    {isInReview ? (
                        <>
                            <AccessTime style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '16px' }} />
                            <Typography variant="h6" style={{ fontWeight: 600, color: '#0f172a', marginBottom: '8px', fontSize: '1.1rem' }}>
                                Revisão em Andamento
                            </Typography>
                            <Typography variant="body2" style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.7, maxWidth: '500px', margin: '0 auto' }}>
                                Seu serviço está passando pela revisão da nossa tecnologia de IA.
                                Os dados da análise ainda não estão disponíveis.
                            </Typography>
                        </>
                    ) : (
                        <>
                            <InfoOutlined style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '16px' }} />
                            <Typography variant="h6" style={{ fontWeight: 600, color: '#0f172a', marginBottom: '8px', fontSize: '1.1rem' }}>
                                Nenhuma Revisão Encontrada
                            </Typography>
                            <Typography variant="body2" style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.7, maxWidth: '500px', margin: '0 auto' }}>
                                Não foi encontrada nenhuma revisão para esta solicitação.
                            </Typography>
                        </>
                    )}
                </div>
                {alert}
            </div>
        );
    }

    if (isApproved) {
        const groupsWithItems = isInternal
            ? faciliterGroups.filter(g => (g.reviews || []).length > 0)
            : faciliterGroups
                .map(g => ({ ...g, reviews: g.reviews.filter(r => r.checklist?.length > 0) }))
                .filter(g => g.reviews.length > 0);

        return (
            <div style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
                <style>{fadeInStyle}</style>
                <ViewHeader />

                <div style={{ padding: '24px', backgroundColor: '#f0fdf4', borderRadius: '8px', marginBottom: '32px', border: '1px solid #bbf7d0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <CheckCircle style={{ fontSize: '1.5rem', color: '#16a34a' }} />
                        <Typography variant="h6" style={{ fontWeight: 600, color: '#166534', fontSize: '1.1rem' }}>
                            Serviço Aprovado na Revisão
                        </Typography>
                    </div>
                    <Typography variant="body2" style={{ color: '#15803d', fontSize: '0.9rem', lineHeight: 1.6, paddingLeft: '42px' }}>
                        {isInternal
                            ? 'O serviço foi analisado e aprovado sem necessidade de ajustes.'
                            : <>Seu serviço foi aprovado pela nossa tecnologia de revisão com IA.<br />Atenção: o cliente poderá solicitar correção do serviço.</>
                        }
                    </Typography>
                </div>

                {groupsWithItems.length > 0 && (
                    <div>
                        <Typography variant="body2" style={{ fontWeight: 600, color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '16px' }}>
                            Histórico de Revisões
                        </Typography>
                        {groupsWithItems.map((group, index) => (
                            <FaciliterGroup
                                key={group.faciliterId}
                                group={group}
                                defaultExpanded={!group.isUnlinked}
                                isInternal={isInternal}
                            />
                        ))}
                    </div>
                )}

                {alert}
            </div>
        );
    }

    // Has reviews with checklist items — main view
    return (
        <div style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
            <style>{fadeInStyle}</style>
            <ViewHeader />

            {/* Faciliter instructions */}
            {isFaciliter && currentReview && (
                <div style={{ marginBottom: '32px', padding: '20px', backgroundColor: '#fef9c3', borderRadius: '8px', border: '1px solid #fde047' }}>
                    <Typography variant="body1" style={{ color: '#713f12', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '16px' }}>
                        Identificamos alguns pontos que precisam ser verificados.
                        Para cada item, indique se você realizou o ajuste ou se considera a sugestão incoerente.
                    </Typography>
                    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Check style={{ fontSize: '16px', color: '#16a34a' }} />
                            <Typography variant="body2" style={{ color: '#713f12', fontSize: '0.85rem' }}>
                                <strong>Ajustei</strong> — realizou o ajuste
                            </Typography>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Close style={{ fontSize: '16px', color: '#dc2626' }} />
                            <Typography variant="body2" style={{ color: '#713f12', fontSize: '0.85rem' }}>
                                <strong>Incoerente</strong> — não condiz com o cadastro do cliente
                            </Typography>
                        </div>
                    </div>
                    <Typography
                        variant="body2"
                        style={{ color: '#854d0e', fontSize: '0.85rem', fontWeight: 500 }}
                    >
                        Caso os ajustes não sejam realizados, você poderá ser desvinculado desta solicitação.
                    </Typography>
                </div>
            )}

            {/* All reviews grouped by faciliter */}
            <div>
                <Typography variant="body2" style={{ fontWeight: 600, color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '16px' }}>
                    Histórico de Revisões
                </Typography>

                {faciliterGroups.map((group, index) => (
                    <FaciliterGroup
                        key={group.faciliterId}
                        group={group}
                        defaultExpanded={!group.isUnlinked}
                        currentReview={currentReview}
                        responses={responses}
                        onResponseChange={handleResponseChange}
                        isInternal={isInternal}
                    />
                ))}
            </div>

            {/* Save button for faciliter */}
            {(isFaciliter && (currentReview || isFromConcludeFlow) || isInternal) && (
                <div style={{ textAlign: 'center', marginTop: '40px', marginBottom: '24px' }}>
                    <Button
                        color="primary"
                        variant="contained"
                        size="large"
                        onClick={handleConfirm}
                        disabled={currentReview ? (!hasValidResponses() || isButtonLoading) : isButtonLoading}
                        style={{
                            padding: '14px 40px',
                            borderRadius: 8,
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            minWidth: '220px',
                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                        }}
                    >
                        {getButtonText()}
                    </Button>
                    <Typography
                        variant="body2"
                        style={{ color: '#64748b', fontSize: '0.85rem', marginTop: '12px', lineHeight: 1.6 }}
                    >
                        Essas informações são fundamentais para manter a qualidade das entregas.
                    </Typography>
                </div>
            )}

            {alert}
        </div>
    );
};

export default RequestServiceReviewView;
